import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, CheckCircle2 } from "lucide-react";
import { cases } from "./CasesPage";
import { breadcrumbSchema, usePageSeo } from "./seo";
const LOGO_URL = "/logo_suanuvem.avif";
function CaseDetailPage() {
  const { caseId } = useParams();
  const caseItem = cases.find(c => c.id === caseId);
  const casePath = `/cases/${caseId}`;

  usePageSeo({
    title: caseItem ? `${caseItem.name}: ${caseItem.title} | Sua Nuvem` : "Case não encontrado | Sua Nuvem",
    description: caseItem
      ? caseItem.description
      : "Conheça cases de sucesso da Sua Nuvem em cloud, DevOps, segurança e infraestrutura.",
    path: casePath,
    structuredData: caseItem
      ? [
          breadcrumbSchema([
            { name: "Início", path: "/" },
            { name: "Cases", path: "/cases" },
            { name: caseItem.name, path: casePath },
          ]),
        ]
      : [],
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [caseId]);

  if (!caseItem) {
    return <Navigate to="/cases" replace />;
  }

  return (
    <div className="min-h-screen bg-brand-dark text-white pt-32 md:pt-40 pb-24 px-6 md:px-12">
      <div className="max-w-5xl mx-auto">
        <Link
          to="/cases"
          className="inline-flex items-center gap-2 text-brand-sage hover:underline mb-8"
          data-testid="back-to-cases"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar para Cases
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Logo */}
          <div className="mb-8">
            <img 
              src={caseItem.logo} 
              alt={caseItem.name}
              className="h-20 w-auto object-contain"
              width="200"
              height="80"
            />
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {caseItem.title}
          </h1>

          {/* Description */}
          <p className="text-xl text-gray-300 leading-relaxed mb-8">
            {caseItem.description}
          </p>

          {/* Services Used */}
          <div className="mb-12">
            <h2 className="text-2xl font-semibold mb-4 text-brand-sage">
              Serviços Utilizados
            </h2>
            <div className="flex flex-wrap gap-3">
              {caseItem.services.map((service, i) => (
                <span
                  key={i}
                  className="px-4 py-2 bg-brand-sage/10 border border-brand-sage/30 rounded-lg text-brand-sage"
                >
                  {service}
                </span>
              ))}
            </div>
          </div>

          {/* Results */}
          <div className="bg-brand-surface/30 border border-white/10 rounded-xl p-8 md:p-12 mb-12">
            <h2 className="text-2xl font-semibold mb-6 text-brand-sage">
              Resultados Alcançados
            </h2>
            <ul className="space-y-4">
              {caseItem.results.map((result, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-brand-sage flex-shrink-0 mt-1" />
                  <span className="text-lg text-gray-300">{result}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* External Link */}
          <div className="text-center">
            <a
              href={caseItem.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-brand-sage text-brand-dark px-8 py-4 rounded-md font-semibold hover:bg-opacity-90 transition-all"
              data-testid="visit-website-button"
            >
              Visitar Website <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default CaseDetailPage;
